import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { getActivePinia } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useSystemStore } from '@/stores/system'
import { useThemeStore } from '@/stores/theme'
import { useTenantAppStore } from '@/stores/tenantApps'
import { resolveSharedAppEntry } from '@shared/apps/manifest'
import {
  guessScopeFromPath,
  isScopeAllowedForRole,
  resolveDefaultTenantRoute,
  type TenantUserScope
} from '@/utils/tenantScope'
import { RESERVED_TENANT_SLUGS, TENANT_SLUG_PATTERN, sanitizeTenantSlug } from '@/constants/tenant'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/Login.vue'),
    meta: { public: true, title: '登录' }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/ForgotPassword.vue'),
    meta: { public: true, title: '找回密码' }
  },
  {
    path: '/:tenant/login',
    name: 'tenant-login',
    component: () => import('@/pages/Login.vue'),
    meta: { public: true, title: '登录' }
  },
  {
    path: '/:tenant/forgot-password',
    name: 'tenant-forgot-password',
    component: () => import('@/pages/ForgotPassword.vue'),
    meta: { public: true, title: '找回密码' }
  },
  {
    path: '/admin',
    component: () => import('@/layouts/foundation/AdminSurfaceShell.vue'),
    meta: { requiresAuth: true, scope: 'admin' },
    children: [
      {
        path: '',
        redirect: { name: 'admin-dashboard' }
      },
      {
        path: 'dashboard',
        name: 'admin-dashboard',
        component: () => import('@/pages/admin/AdminDashboard.vue'),
        meta: { title: '平台概览', icon: 'DataAnalysis', scope: 'admin', affix: true }
      },
      {
        path: 'tenants',
        name: 'admin-tenants',
        component: () => import('@/pages/admin/TenantList.vue'),
        meta: { title: '学校与空间', icon: 'OfficeBuilding', scope: 'admin' }
      },
      {
        path: 'tenant-apps',
        name: 'admin-tenant-apps',
        component: () => import('@/pages/admin/tenant/TenantAppMatrix.vue'),
        meta: { title: '应用授权总览', icon: 'Notebook', scope: 'admin' }
      },
      {
        path: 'tenant-features',
        name: 'admin-tenant-features',
        component: () => import('@/pages/admin/tenant/TenantFeatureCatalog.vue'),
        meta: { title: '功能开关总览', icon: 'SwitchButton', scope: 'admin' }
      },
      {
        path: 'users/all',
        name: 'admin-users-all',
        component: () => import('@/pages/admin/users/UserList.vue'),
        meta: { title: '全部用户', icon: 'User', scope: 'admin', userRole: 'all' }
      },
      {
        path: 'users/analytics',
        name: 'admin-users-analytics',
        component: () => import('@/pages/admin/users/UserAnalytics.vue'),
        meta: { title: '用户统计', icon: 'DataAnalysis', scope: 'admin' }
      },
      {
        path: 'users/tenant-admins',
        name: 'admin-users-tenant-admins',
        component: () => import('@/pages/admin/users/UserList.vue'),
        meta: { title: '管理员', icon: 'UserFilled', scope: 'admin', userRole: 'tenant_admin' }
      },
      {
        path: 'users/teachers',
        name: 'admin-users-teachers',
        component: () => import('@/pages/admin/users/UserList.vue'),
        meta: { title: '教师用户', icon: 'Brush', scope: 'admin', userRole: 'teacher' }
      },
      {
        path: 'users/students',
        name: 'admin-users-students',
        component: () => import('@/pages/admin/users/UserList.vue'),
        meta: { title: '学生用户', icon: 'Document', scope: 'admin', userRole: 'student' }
      },
      {
        path: 'users/others',
        name: 'admin-users-others',
        component: () => import('@/pages/admin/users/UserList.vue'),
        meta: { title: '其他用户', icon: 'User', scope: 'admin', userRole: 'tenant_user' }
      },
      {
        path: 'permissions',
        redirect: { name: 'admin-permission-workbench' },
        meta: { scope: 'admin', hidden: true }
      },
      {
        path: 'permissions/workbench',
        name: 'admin-permission-workbench',
        component: () => import('@/pages/admin/system/PermissionWorkbench.vue'),
        meta: { title: '角色权限', icon: 'Lock', scope: 'admin' }
      },
      {
        path: 'permissions/menus',
        name: 'admin-permission-menus',
        component: () => import('@/pages/admin/system/MenuManagement.vue'),
        meta: { title: '菜单配置', icon: 'Menu', scope: 'admin' }
      },
      {
        path: 'permissions/matrix',
        name: 'admin-permission-matrix',
        component: () => import('@/pages/admin/system/PermissionMatrix.vue'),
        meta: { title: '权限对照表', icon: 'Tickets', scope: 'admin' }
      },
      {
        path: 'permissions/ability-registry',
        name: 'admin-permission-ability-registry',
        component: () => import('@/pages/admin/system/AbilityRegistry.vue'),
        meta: { title: '能力对照表', icon: 'List', scope: 'admin' }
      },
      {
        path: 'permissions/routes',
        name: 'admin-permission-route-audit',
        component: () => import('@/pages/admin/system/RouteAbilityAudit.vue'),
        meta: { title: '路由能力稽核', icon: 'Connection', scope: 'admin' }
      },
      {
        path: 'permissions/app-entitlements',
        name: 'admin-permission-app-entitlements',
        component: () => import('@/pages/admin/system/AppEntitlements.vue'),
        meta: { title: '应用能力包', icon: 'Notebook', scope: 'admin' }
      },
      {
        path: 'tenants/:id',
        name: 'admin-tenant-detail',
        component: () => import('@/pages/admin/TenantDetail.vue'),
        props: true,
        meta: { title: '租户详情', scope: 'admin', hidden: true }
      },
      {
        path: 'messages/templates',
        name: 'admin-message-templates',
        component: () => import('@/pages/admin/messages/Templates.vue'),
        meta: { title: '消息模板', icon: 'CollectionTag', scope: 'admin' }
      },
      {
        path: 'messages/send',
        name: 'admin-message-send',
        component: () => import('@/pages/admin/messages/Send.vue'),
        meta: { title: '消息发送', icon: 'Message', scope: 'admin' }
      },
      {
        path: 'messages/logs',
        name: 'admin-message-logs',
        component: () => import('@/pages/admin/messages/Logs.vue'),
        meta: { title: '发送日志', icon: 'List', scope: 'admin' }
      },
      {
        path: 'messages/statistics',
        name: 'admin-message-statistics',
        component: () => import('@/pages/admin/messages/Statistics.vue'),
        meta: { title: '发送统计', icon: 'Histogram', scope: 'admin' }
      },
      {
        path: 'messages/quotas',
        name: 'admin-message-quotas',
        component: () => import('@/pages/admin/messages/Quotas.vue'),
        meta: { title: '配额监控', icon: 'Histogram', scope: 'admin' }
      },
      {
        path: 'migration/verification',
        name: 'admin-migration-verification',
        component: () => import('@/pages/admin/migration/Verification.vue'),
        meta: { title: '迁移自检', icon: 'List', scope: 'admin', hidden: true }
      },
      {
        path: 'audit/logs',
        name: 'admin-audit-logs',
        component: () => import('@/pages/admin/system/AuditLogs.vue'),
        meta: { title: '审计日志', icon: 'DocumentChecked', scope: 'admin' }
      },
      {
        path: 'login-audits',
        name: 'admin-login-audits',
        component: () => import('@/pages/admin/system/LoginAudits.vue'),
        meta: { title: '登录审计', icon: 'Postcard', scope: 'admin' }
      },
      {
        path: 'alerts/rules',
        name: 'admin-alert-rules',
        component: () => import('@/pages/admin/system/AlertRules.vue'),
        meta: { title: '告警规则', icon: 'WarningFilled', scope: 'admin' }
      },
      {
        path: 'system/dictionaries',
        name: 'admin-dictionaries',
        component: () => import('@/pages/admin/system/DictionaryManagement.vue'),
        meta: { title: '业务字典', icon: 'CollectionTag', scope: 'admin' }
      },
      {
        path: 'system/settings',
        name: 'admin-system-settings',
        component: () => import('@/pages/admin/system/SystemSettings.vue'),
        meta: { title: '平台配置', icon: 'Setting', scope: 'admin' }
      },
      {
        path: 'system/ai-models',
        name: 'admin-system-ai-models',
        component: () => import('@/pages/admin/system/AiModelManagement.vue'),
        meta: { title: 'AI 模型管理', icon: 'Connection', scope: 'admin' }
      },
      {
        path: 'system/ai-personas',
        name: 'admin-system-ai-personas',
        component: () => import('@/pages/admin/system/AiPersonaManagement.vue'),
        meta: { title: 'AI 角色账号', icon: 'Avatar', scope: 'admin' }
      },
      {
        path: 'system/announcements',
        name: 'admin-announcements',
        component: () => import('@/pages/admin/system/Announcements.vue'),
        meta: { title: '平台公告', icon: 'Bell', scope: 'admin' }
      },
      {
        path: 'system/regions',
        name: 'admin-regions',
        component: () => import('@/pages/admin/system/RegionManagement.vue'),
        meta: { title: '行政区划', icon: 'Location', scope: 'admin' }
      },
      {
        path: 'system/files/statistics',
        name: 'admin-file-statistics',
        component: () => import('@/pages/admin/system/FileStatistics.vue'),
        meta: { title: '文件统计', icon: 'Histogram', scope: 'admin' }
      },
      {
        path: 'system/files',
        name: 'admin-files',
        component: () => import('@/pages/admin/system/FileManagement.vue'),
        meta: { title: '文件管理', icon: 'Folder', scope: 'admin' }
      },
      {
        path: 'system/roles',
        redirect: { name: 'admin-permission-workbench' },
        meta: { scope: 'admin', hidden: true }
      },
      {
        path: 'system/super-users',
        name: 'admin-super-users',
        component: () => import('@/pages/admin/system/SuperAdminManagement.vue'),
        meta: { title: '超管用户', icon: 'UserFilled', scope: 'admin' }
      },
      {
        path: 'system/menus',
        redirect: { name: 'admin-permission-menus' },
        meta: { scope: 'admin', hidden: true }
      },
      {
        path: 'system/operations',
        redirect: { name: 'admin-system-operations-schedules' },
        meta: { scope: 'admin', hidden: true }
      },
      {
        path: 'system/operations/schedules',
        name: 'admin-system-operations-schedules',
        component: () => import('@/pages/admin/operations/ScheduleManagement.vue'),
        meta: { title: '计划任务', icon: 'Timer', scope: 'admin' }
      },
      {
        path: 'system/operations/resources',
        name: 'admin-system-operations-resources',
        component: () => import('@/pages/admin/operations/ServerResourceManagement.vue'),
        meta: { title: '服务器资源', icon: 'Cpu', scope: 'admin' }
      },
      {
        path: 'system/operations/queues',
        name: 'admin-system-operations-queues',
        component: () => import('@/pages/admin/operations/QueueManagement.vue'),
        meta: { title: '发送队列', icon: 'Connection', scope: 'admin' }
      },
      {
        path: 'system/operations/online-users',
        name: 'admin-system-operations-online-users',
        component: () => import('@/pages/admin/operations/OnlineUsers.vue'),
        meta: { title: '在线用户', icon: 'UserFilled', scope: 'admin' }
      },
      {
        path: 'system/operations/migration-verification',
        name: 'admin-system-operations-migration',
        component: () => import('@/pages/admin/migration/Verification.vue'),
        meta: { title: '迁移自检', icon: 'List', scope: 'admin' }
      },
      {
        path: 'profile',
        name: 'admin-profile',
        component: () => import('@/pages/Profile.vue'),
        props: { scope: 'admin' },
        meta: { title: '个人资料', icon: 'User', scope: 'admin' }
      },
      {
        path: 'personal/files',
        name: 'admin-personal-files',
        component: () => import('@/pages/admin/personal/AdminFiles.vue'),
        meta: { title: '我的文档', icon: 'Folder', scope: 'admin' }
      },
      {
        path: 'personal/notifications',
        name: 'admin-notifications',
        component: () => import('@/pages/admin/personal/NotificationCenter.vue'),
        meta: { title: '通知消息', icon: 'Bell', scope: 'admin' }
      }
    ]
  },
  {
    path: '/tenant/:tenant',
    component: () => import('@/layouts/foundation/FoundationSurfaceShell.vue'),
    meta: { requiresAuth: true, tenantScope: 'manage' },
    children: [
      {
        path: '',
        redirect: { name: 'tenant-manage-dashboard' }
      },
      {
        path: 'dashboard',
        name: 'tenant-manage-dashboard',
        component: () => import('@/pages/Dashboard.vue'),
        meta: { title: '概览', icon: 'DataAnalysis', tenantScope: 'manage', affix: true }
      },
      {
        path: 'apps',
        name: 'tenant-manage-apps',
        component: () => import('@/pages/tenant/AppLaunchpad.vue'),
        meta: { title: '应用中心', icon: 'Grid', tenantScope: 'manage' }
      },
      {
        path: 'jy/docs',
        name: 'tenant-manage-jy-docs',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '教研文档', icon: 'Document', tenantScope: 'manage', tenantApp: 'jy', hidden: true }
      },
      {
        path: 'jy/analytics',
        name: 'tenant-manage-jy-analytics',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '教研分析', icon: 'DataAnalysis', tenantScope: 'manage', tenantApp: 'jy', hidden: true }
      },
      {
        path: 'jy/docs/:id/structure',
        name: 'tenant-manage-jy-doc-structure',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '教学方案结构', icon: 'Document', tenantScope: 'manage', tenantApp: 'jy', hidden: true }
      },
      {
        path: 'jy/docs/:id/console',
        name: 'tenant-manage-jy-doc-console',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '课堂控制台', icon: 'Monitor', tenantScope: 'manage', tenantApp: 'jy', hidden: true }
      },
      {
        path: 'kt/classes',
        name: 'tenant-manage-kt-classes',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '班级中心', icon: 'CollectionTag', tenantScope: 'manage', tenantApp: 'kt', hidden: true }
      },
      {
        path: 'kt/assignments',
        name: 'tenant-manage-kt-assignments',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '课堂作业管理', icon: 'Notebook', tenantScope: 'manage', tenantApp: 'kt', hidden: true }
      },
      {
        path: 'kt/lessons',
        name: 'tenant-manage-kt-lessons',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '课程日程管理', icon: 'Timer', tenantScope: 'manage', tenantApp: 'kt', hidden: true }
      },
      {
        path: 'kt/interaction',
        name: 'tenant-manage-kt-interaction',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '课堂互动管理', icon: 'ChatDotRound', tenantScope: 'manage', tenantApp: 'kt', hidden: true }
      },
      {
        path: 'kt/scores',
        name: 'tenant-manage-kt-scores',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '成绩看板', icon: 'TrendCharts', tenantScope: 'manage', tenantApp: 'kt', hidden: true }
      },
      {
        path: 'kt/groups',
        name: 'tenant-manage-kt-groups',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '班级分组', icon: 'UserFilled', tenantScope: 'manage', tenantApp: 'kt', hidden: true }
      },
      {
        path: 'match/dashboard',
        name: 'tenant-manage-match-dashboard',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '赛事实时看板', icon: 'Trophy', tenantScope: 'manage', tenantApp: 'match', hidden: true }
      },
      {
        path: 'match/competitions',
        name: 'tenant-manage-match-competitions',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '赛事列表', icon: 'Trophy', tenantScope: 'manage', tenantApp: 'match', hidden: true }
      },
      {
        path: 'match/competitions/:competitionId',
        name: 'tenant-manage-match-competition-detail',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '赛事详情', icon: 'Trophy', tenantScope: 'manage', tenantApp: 'match', hidden: true }
      },
      {
        path: 'match/signups',
        name: 'tenant-manage-match-signups',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '报名审核', icon: 'DocumentChecked', tenantScope: 'manage', tenantApp: 'match', hidden: true }
      },
      {
        path: 'match/signups/:signupId',
        name: 'tenant-manage-match-signup-detail',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '报名详情', icon: 'DocumentChecked', tenantScope: 'manage', tenantApp: 'match', hidden: true }
      },
      {
        path: 'match/projects',
        name: 'tenant-manage-match-projects',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '项目管理', icon: 'Collection', tenantScope: 'manage', tenantApp: 'match', hidden: true }
      },
      {
        path: 'match/projects/:projectId',
        name: 'tenant-manage-match-project-detail',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '项目详情', icon: 'Collection', tenantScope: 'manage', tenantApp: 'match', hidden: true }
      },
      {
        path: 'school',
        redirect: { name: 'tenant-manage-school-profile' }
      },
      {
        path: 'school/profile',
        name: 'tenant-manage-school-profile',
        component: () => import('@/pages/tenant/manage/SchoolProfile.vue'),
        meta: { title: '基础设置', icon: 'OfficeBuilding', tenantScope: 'manage' }
      },
      {
        path: 'school/spaces',
        name: 'tenant-manage-school-spaces',
        component: () => import('@/pages/tenant/manage/SchoolSpaces.vue'),
        meta: { title: '校区与空间', icon: 'Location', tenantScope: 'manage' }
      },
      {
        path: 'school/orgs',
        name: 'tenant-manage-school-orgs',
        component: () => import('@/pages/tenant/manage/SchoolOrgs.vue'),
        meta: { title: '组织机构', icon: 'CollectionTag', tenantScope: 'manage' }
      },
      {
        path: 'school/apps',
        name: 'tenant-manage-school-apps',
        component: () => import('@/pages/tenant/manage/AppAuthorization.vue'),
        meta: { title: '应用授权', icon: 'SwitchButton', tenantScope: 'manage' }
      },
      {
        path: 'school/ai-learning',
        name: 'tenant-manage-school-ai-learning',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '智学成长', icon: 'DataAnalysis', tenantScope: 'manage', tenantApp: 'ai-learning', hidden: true }
      },
      {
        path: 'school/ai-model',
        name: 'tenant-manage-school-ai-model',
        component: () => import('@/pages/tenant/manage/AiModelConfig.vue'),
        meta: { title: 'AI 模型配置', icon: 'Connection', tenantScope: 'manage' }
      },
      {
        path: 'school/ai-personas',
        name: 'tenant-manage-school-ai-personas',
        component: () => import('@/pages/tenant/manage/AiPersonaConfig.vue'),
        meta: { title: 'AI 角色账号', icon: 'Avatar', tenantScope: 'manage' }
      },
      {
        path: 'school/auth',
        name: 'tenant-manage-school-auth',
        component: () => import('@/pages/tenant/manage/AuthConfig.vue'),
        meta: { title: '统一认证', icon: 'Lock', tenantScope: 'manage' }
      },
      {
        path: 'users',
        redirect: { name: 'tenant-manage-users-admins' }
      },
      {
        path: 'users/admins',
        name: 'tenant-manage-users-admins',
        component: () => import('@/pages/tenant/manage/UserManagement.vue'),
        meta: { title: '管理员', icon: 'UserFilled', tenantScope: 'manage', userRole: 'tenant_admin' }
      },
      {
        path: 'users/teachers',
        name: 'tenant-manage-users-teachers',
        component: () => import('@/pages/tenant/manage/UserManagement.vue'),
        meta: { title: '教师账号', icon: 'Brush', tenantScope: 'manage', userRole: 'teacher' }
      },
      {
        path: 'users/students',
        name: 'tenant-manage-users-students',
        component: () => import('@/pages/tenant/manage/UserManagement.vue'),
        meta: { title: '学生账号', icon: 'Document', tenantScope: 'manage', userRole: 'student' }
      },
      {
        path: 'users/others',
        name: 'tenant-manage-users-others',
        component: () => import('@/pages/tenant/manage/UserManagement.vue'),
        meta: { title: '其他账号', icon: 'User', tenantScope: 'manage', userRole: 'tenant_user' }
      },
      {
        path: 'users/roles',
        name: 'tenant-manage-users-roles',
        component: () => import('@/pages/tenant/manage/RoleManagement.vue'),
        meta: { title: '角色权限', icon: 'Lock', tenantScope: 'manage' }
      },
      {
        path: 'profile',
        name: 'tenant-manage-profile',
        component: () => import('@/pages/Profile.vue'),
        meta: { title: '个人资料', icon: 'User', tenantScope: 'manage' }
      },
      {
        path: 'files',
        name: 'tenant-manage-files',
        component: () => import('@/pages/Files.vue'),
        meta: { title: '我的文档', icon: 'Folder', tenantScope: 'manage' }
      },
      {
        path: 'notifications',
        name: 'tenant-manage-notifications',
        component: () => import('@/pages/tenant/manage/NotificationCenter.vue'),
        meta: { title: '通知消息', icon: 'Bell', tenantScope: 'manage' }
      },
      {
        path: 'messages/send',
        name: 'tenant-manage-message-console',
        component: () => import('@/pages/MessageConsole.vue'),
        meta: { title: '消息发送', icon: 'Message', tenantScope: 'manage' }
      },
      {
        path: 'messages/logs',
        name: 'tenant-manage-message-logs',
        component: () => import('@/pages/messages/MessageLogs.vue'),
        meta: { title: '发送日志', icon: 'List', tenantScope: 'manage' }
      },
      {
        path: 'messages/statistics',
        name: 'tenant-manage-message-statistics',
        component: () => import('@/pages/messages/MessageStatistics.vue'),
        meta: { title: '发送统计', icon: 'Histogram', tenantScope: 'manage' }
      },
      {
        path: 'messages/quotas',
        name: 'tenant-manage-message-quotas',
        component: () => import('@/pages/messages/MessageQuotas.vue'),
        meta: { title: '配额监控', icon: 'Monitor', tenantScope: 'manage' }
      },
      {
        path: ':pathMatch(.*)*',
        name: 'tenant-manage-not-found',
        component: () => import('@/pages/NotFound.vue'),
        meta: { title: '页面未找到', tenantScope: 'manage' }
      }
    ]
  },
  {
    path: '/tenant/:tenant/manage',
    redirect: (to) => ({
      name: 'tenant-manage-dashboard',
      params: { tenant: to.params.tenant }
    })
  },
  {
    path: '/tenant/:tenant/manage/:pathMatch(.*)*',
    redirect: (to) => {
      const tenant = to.params.tenant
      const remainder = Array.isArray(to.params.pathMatch)
        ? to.params.pathMatch.join('/')
        : (to.params.pathMatch as string | undefined) ?? ''
      if (!tenant) {
        return { path: '/login' }
      }
      if (!remainder) {
        return `/tenant/${tenant}/dashboard`
      }
      return `/tenant/${tenant}/${remainder}`
    }
  },
  {
    path: '/tenant/:tenant/teacher',
    component: () => import('@/layouts/foundation/FoundationSurfaceShell.vue'),
    meta: { requiresAuth: true, tenantScope: 'teacher' },
    children: [
      {
        path: '',
        redirect: { name: 'tenant-teacher-dashboard' }
      },
      {
        path: 'dashboard',
        name: 'tenant-teacher-dashboard',
        component: () => import('@/pages/teacher/TeacherDashboard.vue'),
        meta: { title: '教学看板', icon: 'DataAnalysis', tenantScope: 'teacher', tenantFeature: 'teacher.dashboard' }
      },
      {
        path: 'ai-learning',
        name: 'tenant-teacher-ai-learning',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '智学成长', icon: 'DataAnalysis', tenantScope: 'teacher', tenantFeature: 'teacher.ai-learning', tenantApp: 'ai-learning', hidden: true }
      },
      {
        path: 'apps',
        name: 'tenant-teacher-apps',
        component: () => import('@/pages/tenant/AppLaunchpad.vue'),
        meta: { title: '应用中心', icon: 'Grid', tenantScope: 'teacher' }
      },
      {
        path: 'profile',
        name: 'tenant-teacher-profile',
        component: () => import('@/pages/Profile.vue'),
        meta: { title: '个人资料', icon: 'User', tenantScope: 'teacher', tenantFeature: 'teacher.personal.profile' }
      },
      {
        path: 'files',
        name: 'tenant-teacher-files',
        component: () => import('@/pages/Files.vue'),
        meta: { title: '我的文档', icon: 'Folder', tenantScope: 'teacher', tenantFeature: 'teacher.personal.files' }
      },
      {
        path: 'notifications',
        name: 'tenant-teacher-notifications',
        component: () => import('@/pages/tenant/manage/NotificationCenter.vue'),
        meta: { title: '通知消息', icon: 'Bell', tenantScope: 'teacher', tenantFeature: 'teacher.personal.notifications' }
      },
      {
        path: 'jy/docs',
        name: 'tenant-teacher-docs',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '教研文档', icon: 'Document', tenantScope: 'teacher', tenantFeature: 'teacher.jy.docs', tenantApp: 'jy', hidden: true }
      },
      {
        path: 'jy/docs/:id/structure',
        name: 'tenant-teacher-jy-doc-structure',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '教学方案结构', icon: 'Document', tenantScope: 'teacher', tenantFeature: 'teacher.jy.docs', tenantApp: 'jy', hidden: true }
      },
      {
        path: 'jy/docs/:id/console',
        name: 'tenant-teacher-jy-doc-console',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '课堂控制台', icon: 'Monitor', tenantScope: 'teacher', tenantFeature: 'teacher.jy.docs', tenantApp: 'jy', hidden: true }
      },
      {
        path: 'kt/classes',
        name: 'tenant-teacher-classes',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '班级中心',
          icon: 'CollectionTag',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.kt.lessons',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/assignments',
        name: 'tenant-teacher-assignments',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '课堂作业',
          icon: 'Notebook',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.kt.assignments',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/lessons',
        name: 'tenant-teacher-lessons',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '课程日程',
          icon: 'Timer',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.kt.lessons',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/interaction',
        name: 'tenant-teacher-interaction',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '课堂互动',
          icon: 'ChatDotRound',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.kt.lessons',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/scores',
        name: 'tenant-teacher-scores',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '成绩看板',
          icon: 'TrendCharts',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.kt.assignments',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/groups',
        name: 'tenant-teacher-groups',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '班级分组',
          icon: 'UserFilled',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.kt.assignments',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'match/dashboard',
        name: 'tenant-teacher-competitions',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '赛事动态',
          icon: 'Trophy',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.match.realtime',
          tenantApp: 'match', hidden: true
        }
      },
      {
        path: 'match/competitions/:competitionId',
        name: 'tenant-teacher-match-competition-detail',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '赛事详情',
          icon: 'Trophy',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.match.realtime',
          tenantApp: 'match',
          hidden: true
        }
      },
      {
        path: 'match/participation',
        name: 'tenant-teacher-match-participation',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '报名参赛',
          icon: 'EditPen',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.match.participate',
          tenantApp: 'match',
          hidden: true
        }
      },
      {
        path: 'match/participation/competitions/:competitionId/signup',
        name: 'tenant-teacher-match-participation-signup',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '赛事报名',
          icon: 'EditPen',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.match.participate',
          tenantApp: 'match',
          hidden: true
        }
      },
      {
        path: 'match/participation/projects',
        name: 'tenant-teacher-match-participation-projects',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '本人项目',
          icon: 'FolderChecked',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.match.participate',
          tenantApp: 'match',
          hidden: true
        }
      },
      {
        path: 'match/projects',
        name: 'tenant-teacher-match-projects',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '评审项目',
          icon: 'Collection',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.match.projects',
          tenantApp: 'match', hidden: true
        }
      },
      {
        path: 'match/projects/:projectId',
        name: 'tenant-teacher-match-project-detail',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '项目评审',
          icon: 'Collection',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.match.projects',
          tenantApp: 'match',
          hidden: true
        }
      },
      {
        path: 'match/projects/:projectId/score',
        name: 'tenant-teacher-match-project-score',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '现场评分',
          icon: 'EditPen',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.match.projects',
          tenantApp: 'match',
          hidden: true
        }
      },
      {
        path: 'match/realtime',
        name: 'tenant-teacher-match-realtime',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '赛事实时看板',
          icon: 'Trophy',
          tenantScope: 'teacher',
          tenantFeature: 'teacher.match.realtime',
          tenantApp: 'match', hidden: true
        }
      },
      {
        path: ':pathMatch(.*)*',
        name: 'tenant-teacher-not-found',
        component: () => import('@/pages/NotFound.vue'),
        meta: { title: '页面未找到', tenantScope: 'teacher' }
      }
    ]
  },
  {
    path: '/tenant/:tenant/student',
    component: () => import('@/layouts/foundation/FoundationSurfaceShell.vue'),
    meta: { requiresAuth: true, tenantScope: 'student' },
    children: [
      {
        path: '',
        redirect: { name: 'tenant-student-dashboard' }
      },
      {
        path: 'dashboard',
        name: 'tenant-student-dashboard',
        component: () => import('@/pages/student/StudentDashboard.vue'),
        meta: { title: '学习看板', icon: 'DataAnalysis', tenantScope: 'student', tenantFeature: 'student.dashboard' }
      },
      {
        path: 'ai-learning',
        name: 'tenant-student-ai-learning',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '智学成长', icon: 'DataAnalysis', tenantScope: 'student', tenantFeature: 'student.ai-learning', tenantApp: 'ai-learning', hidden: true }
      },
      {
        path: 'apps',
        name: 'tenant-student-apps',
        component: () => import('@/pages/tenant/AppLaunchpad.vue'),
        meta: { title: '应用中心', icon: 'Grid', tenantScope: 'student' }
      },
      {
        path: 'profile',
        name: 'tenant-student-profile',
        component: () => import('@/pages/Profile.vue'),
        meta: { title: '个人资料', icon: 'User', tenantScope: 'student', tenantFeature: 'student.personal.profile' }
      },
      {
        path: 'files',
        name: 'tenant-student-files',
        component: () => import('@/pages/Files.vue'),
        meta: { title: '我的文档', icon: 'Folder', tenantScope: 'student', tenantFeature: 'student.personal.files' }
      },
      {
        path: 'notifications',
        name: 'tenant-student-notifications',
        component: () => import('@/pages/tenant/manage/NotificationCenter.vue'),
        meta: { title: '通知消息', icon: 'Bell', tenantScope: 'student', tenantFeature: 'student.personal.notifications' }
      },
      {
        path: 'kt/dashboard',
        name: 'tenant-student-kt-dashboard',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '课堂首页',
          icon: 'DataAnalysis',
          tenantScope: 'student',
          tenantFeature: 'student.kt.assignments',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/assignments',
        name: 'tenant-student-assignments',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '我的作业',
          icon: 'Notebook',
          tenantScope: 'student',
          tenantFeature: 'student.kt.assignments',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/interactions',
        name: 'tenant-student-interactions',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '课堂互动',
          icon: 'ChatDotRound',
          tenantScope: 'student',
          tenantFeature: 'student.kt.assignments',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/groups',
        name: 'tenant-student-groups',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '我的分组',
          icon: 'UserFilled',
          tenantScope: 'student',
          tenantFeature: 'student.kt.assignments',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/openworks',
        name: 'tenant-student-openworks',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '开放任务',
          icon: 'FolderChecked',
          tenantScope: 'student',
          tenantFeature: 'student.kt.assignments',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/attendance',
        name: 'tenant-student-attendance',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '我的考勤',
          icon: 'Timer',
          tenantScope: 'student',
          tenantFeature: 'student.kt.assignments',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'kt/scores',
        name: 'tenant-student-scores',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '我的成绩',
          icon: 'TrendCharts',
          tenantScope: 'student',
          tenantFeature: 'student.kt.assignments',
          tenantApp: 'kt', hidden: true
        }
      },
      {
        path: 'jy/docs',
        name: 'tenant-student-resources',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '教研资源',
          icon: 'Document',
          tenantScope: 'student',
          tenantFeature: 'student.jy.docs',
          tenantApp: 'jy', hidden: true
        }
      },
      {
        path: 'match/dashboard',
        name: 'tenant-student-competitions',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '赛事资讯',
          icon: 'Trophy',
          tenantScope: 'student',
          tenantFeature: 'student.match.info',
          tenantApp: 'match', hidden: true
        }
      },
      {
        path: 'match/competitions/:competitionId/signup',
        name: 'tenant-student-match-signup',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '赛事报名',
          icon: 'EditPen',
          tenantScope: 'student',
          tenantFeature: 'student.match.info',
          tenantApp: 'match',
          hidden: true
        }
      },
      {
        path: 'match/competitions/:competitionId/official',
        name: 'tenant-student-match-official',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '赛事官网',
          icon: 'Trophy',
          tenantScope: 'student',
          tenantFeature: 'student.match.info',
          tenantApp: 'match',
          hidden: true
        }
      },
      {
        path: 'match/projects',
        name: 'tenant-student-match-projects',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: {
          title: '我的项目',
          icon: 'FolderChecked',
          tenantScope: 'student',
          tenantFeature: 'student.match.info',
          tenantApp: 'match', hidden: true
        }
      },
      {
        path: ':pathMatch(.*)*',
        name: 'tenant-student-not-found',
        component: () => import('@/pages/NotFound.vue'),
        meta: { title: '页面未找到', tenantScope: 'student' }
      }
    ]
  },
  {
    path: '/tenant/:tenant/user',
    component: () => import('@/layouts/foundation/FoundationSurfaceShell.vue'),
    meta: { requiresAuth: true, tenantScope: 'user' },
    children: [
      {
        path: '',
        redirect: { name: 'tenant-user-dashboard' }
      },
      {
        path: 'dashboard',
        name: 'tenant-user-dashboard',
        component: () => import('@/pages/user/UserDashboard.vue'),
        meta: { title: '协作看板', icon: 'DataAnalysis', tenantScope: 'user', tenantFeature: 'user.dashboard' }
      },
      {
        path: 'apps',
        name: 'tenant-user-apps',
        component: () => import('@/pages/tenant/AppLaunchpad.vue'),
        meta: { title: '应用中心', icon: 'Grid', tenantScope: 'user' }
      },
      {
        path: 'profile',
        name: 'tenant-user-profile',
        component: () => import('@/pages/Profile.vue'),
        meta: { title: '个人资料', icon: 'User', tenantScope: 'user', tenantFeature: 'user.personal.profile' }
      },
      {
        path: 'files',
        name: 'tenant-user-files',
        component: () => import('@/pages/Files.vue'),
        meta: { title: '我的文档', icon: 'Folder', tenantScope: 'user', tenantFeature: 'user.personal.files' }
      },
      {
        path: 'notifications',
        name: 'tenant-user-notifications',
        component: () => import('@/pages/tenant/manage/NotificationCenter.vue'),
        meta: { title: '通知消息', icon: 'Bell', tenantScope: 'user', tenantFeature: 'user.personal.notifications' }
      },
      {
        path: 'match/dashboard',
        name: 'tenant-user-match-competitions',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '赛事资讯', icon: 'Trophy', tenantScope: 'user', tenantFeature: 'external.match.participate', tenantApp: 'match', hidden: true }
      },
      {
        path: 'match/competitions/:competitionId/signup',
        name: 'tenant-user-match-signup',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '赛事报名', icon: 'EditPen', tenantScope: 'user', tenantFeature: 'external.match.participate', tenantApp: 'match', hidden: true }
      },
      {
        path: 'match/projects',
        name: 'tenant-user-match-projects',
        component: () => import('@/pages/tenant/AppRedirect.vue'),
        meta: { title: '本人项目', icon: 'FolderChecked', tenantScope: 'user', tenantFeature: 'external.match.participate', tenantApp: 'match', hidden: true }
      },
      {
        path: ':pathMatch(.*)*',
        name: 'tenant-user-not-found',
        component: () => import('@/pages/NotFound.vue'),
        meta: { title: '页面未找到', tenantScope: 'user' }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: (to) => {
      const raw = to.params.pathMatch
      const segments = Array.isArray(raw) ? raw : (raw ? String(raw).split('/') : [])
      const first = segments[0]
      const reserved = new Set<string>(RESERVED_TENANT_SLUGS)
      const slugPattern = TENANT_SLUG_PATTERN
      if (
        first &&
        segments.length === 1 &&
        slugPattern.test(first) &&
        !reserved.has(first)
      ) {
        return {
          name: 'tenant-login',
          params: { tenant: first },
          query: to.query
        }
      }
      return { path: '/login' }
    }
  }
]

export const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  const rawTenantParam = Array.isArray(to.params.tenant) ? to.params.tenant[0] : (to.params.tenant as string | undefined)
  const rawTenantQuery = Array.isArray(to.query.tenant) ? to.query.tenant[0] : (to.query.tenant as string | undefined)
  const routeTenant = sanitizeTenantSlug(rawTenantParam || rawTenantQuery)

  const hasSession = Boolean(auth.token)
  const currentTenant = auth.tenantSlug || ''

  if (routeTenant) {
    const allowSyncTenant = !hasSession || !currentTenant || currentTenant === routeTenant
    if (allowSyncTenant) {
      auth.setTenantSlug(routeTenant)
    }
  }

  const sessionTenant = auth.profile?.tenant_slug || auth.tenantSlug || ''

  if (to.meta.public) {
    const isLoginView = to.name === 'login' || to.name === 'tenant-login'
    if (isLoginView && auth.token) {
      const fallbackSlug = sessionTenant || routeTenant || undefined
      try {
        await auth.ensureSession(fallbackSlug)
        if (auth.profile?.role === 'super_admin') {
          return { path: '/admin/dashboard' }
        }
        const effectiveSlug = auth.profile?.tenant_slug || auth.tenantSlug || fallbackSlug
        const redirectPath = resolveDefaultTenantRoute(auth.profile?.role, effectiveSlug)
        return { path: redirectPath }
      } catch (error) {
        console.warn('尝试复用已登录会话失败，将停留在登录页', error)
        auth.logout()
      }
    }
    return true
  }

  if (!auth.token) {
    return {
      path: '/login',
      query: {
        redirect: to.fullPath,
        tenant: routeTenant ?? ''
      }
    }
  }

  try {
    const preferredTenant = sessionTenant || routeTenant || undefined
    await auth.ensureSession(preferredTenant)
  } catch (error) {
    console.warn('初始化会话失败', error)
    auth.logout()
    return {
      path: '/login',
      query: {
        redirect: to.fullPath,
        tenant: routeTenant ?? ''
      }
    }
  }

  const effectiveTenantSlug = auth.profile?.tenant_slug || auth.tenantSlug || ''
  if (routeTenant && effectiveTenantSlug && routeTenant !== effectiveTenantSlug) {
    if (auth.profile?.role !== 'super_admin') {
      if (to.name) {
        return {
          name: to.name,
          params: { ...to.params, tenant: effectiveTenantSlug },
          query: to.query,
          hash: to.hash
        }
      }
      const redirectedPath = resolveDefaultTenantRoute(auth.profile?.role, effectiveTenantSlug)
      if (redirectedPath && redirectedPath !== to.fullPath) {
        return { path: redirectedPath }
      }
    }
  }

  const fallbackSlug = effectiveTenantSlug || routeTenant
  const effectiveScope: TenantUserScope | undefined =
    (to.meta?.tenantScope as TenantUserScope | undefined) ??
    (to.path.startsWith('/tenant/') ? guessScopeFromPath(to.path) : undefined)

  if (effectiveScope && !isScopeAllowedForRole(effectiveScope, auth.profile?.role)) {
    if (auth.profile?.role === 'super_admin') {
      return { path: '/admin/dashboard' }
    }
    const fallbackPath = resolveDefaultTenantRoute(auth.profile?.role, fallbackSlug)
    if (fallbackPath && fallbackPath !== to.fullPath) {
      return { path: fallbackPath }
    }
  }

  const pinia = getActivePinia()
  const appStore = pinia ? useTenantAppStore() : useTenantAppStore()
  const requiredFeature = (to.meta?.tenantFeature as string | string[] | undefined) ?? undefined
  if (requiredFeature && auth.profile?.role !== 'super_admin') {
    await appStore.ensureApps(effectiveTenantSlug || fallbackSlug)
    if (!appStore.isFeatureEnabled(requiredFeature) && !auth.hasCapability(requiredFeature)) {
      if (auth.profile?.role === 'super_admin') {
        return { path: '/admin/dashboard' }
      }
      const fallbackPath = resolveDefaultTenantRoute(auth.profile?.role, fallbackSlug)
      if (fallbackPath && fallbackPath !== to.fullPath) {
        return { path: fallbackPath }
      }
      return { name: 'login', query: { tenant: routeTenant ?? '' } }
    }
  }

  const requiredApp = (to.meta?.tenantApp as string | undefined) ?? undefined
  if (requiredApp && auth.profile?.role !== 'super_admin') {
    await appStore.ensureApps(effectiveTenantSlug || fallbackSlug)
    const appEntry = resolveSharedAppEntry(requiredApp)
    if (!appStore.isAppEnabled(requiredApp) || !appStore.isFeatureOrCapabilityEnabled(appEntry)) {
      const fallbackPath = resolveDefaultTenantRoute(auth.profile?.role, fallbackSlug)
      if (fallbackPath && fallbackPath !== to.fullPath) {
        return { path: fallbackPath }
      }
      return { name: 'login', query: { tenant: routeTenant ?? '' } }
    }
  }

  return true
})

router.afterEach((to) => {
  const systemStore = useSystemStore()
  const base = systemStore.displayName
  const title = (to.meta?.title as string) || ''
  const pinia = getActivePinia()
  const themeStore = pinia ? useThemeStore() : null
  const enableDynamic = themeStore?.state.dynamicTitle !== false
  document.title = enableDynamic && title ? `${title} - ${base}` : base
})

export default router





